// v4 Task #11 — Project Status Update workflow tab.
//
// Per-project tab showing every currently-marked Item + Action (the
// "draft preview" surface — Section 5.7). The .docx generation
// pipeline lands in Task #12; this tab is the mark management +
// preview view.
//
// Mark toggling reuses the v4 correction kinds from Task #8/9 wired
// through the existing /feedback/:runId path. Bulk "Select all" on
// an Item header iterates the kil_mark POST per child Action — no
// new server-side endpoint needed.

import {
  ApiError, downloadFile, getJson, postJson, wireDownloadControl,
} from "./api";
import {
  dueMetaBit,
  PHASE_LABELS,
  PHASE_ORDER,
  phaseBand,
  renderBreadcrumb,
  type BreadcrumbView,
  type PhaseValue,
} from "./briefings";

// Decision 5.4 bulk-mark shortcut ("Select all child Actions" on the KIL
// tab, "Mark all (N) for KIL" on the briefing Item header). Hidden by
// default per operator request — the render is distracting before
// clients have asked for it. Flip both flags to true to restore.
// The implementation, CSS, and 14 vitest cases all stay intact.
const BULK_KIL_MARK_VISIBLE = false;

// v4 Task #12 — generation archive entries. Listing returns metadata
// only (no blobs); the SPA renders a Download link that hits
// /kil-generations/:id/download for the bytes.
export interface KilGenerationListItem {
  id: string;
  generated_at: string;
  generated_by: string;
  week_end: string | null;
  filename: string;
  label: string | null | undefined;
  marked_items?: number;
  marked_actions?: number;
  // #111b — which download format(s) this generation carries. The single
  // download icon serves Excel when present, else Word (historical records).
  has_xlsx?: boolean;
  has_docx?: boolean;
}

export interface KilGenerationsResponse {
  project_slug: string;
  generations: KilGenerationListItem[];
}

export async function fetchKilGenerations(): Promise<KilGenerationsResponse> {
  return getJson<KilGenerationsResponse>("/kil-generations");
}

export interface KilGenerationCreateResponse {
  id: string;
  generated_at: string;
  filename: string;
  label: string | null | undefined;
}

export async function createKilGeneration(
  filename: string, label: string | null,
): Promise<KilGenerationCreateResponse> {
  return postJson<KilGenerationCreateResponse>("/kil-generations", {
    filename, label,
  });
}

export function kilGenerationDownloadUrl(id: string): string {
  // Resolved against the same Worker origin the SPA already uses for
  // /briefings/:id/download — the api.ts wrapper prepends WORKER_URL.
  return `/kil-generations/${encodeURIComponent(id)}/download`;
}

export interface KilAction {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  ai_slug: string;
  serial: number | null;
  display_number: string | null;
  text: string;
  narrative: string | null;
  priority_status: string;
  status: "open" | "closed";
  assignee: string | null;
  due: string | null;
  first_seen: string | null;
  closed_at: string | null;
  reason: string | null;
  impact: string | null;
  off_hold_condition: string | null;
  kil_marked: boolean;
  action_type: string;
  // NOTE: `open_questions_v4` is intentionally absent. The agent stopped
  // emitting it into the marks snapshot on 2026-08-12 (client: no questions on
  // the PSU), and the field is not read anywhere in this view. Keeping the
  // declaration would invite a future renderer to reach for it and silently
  // resurrect the behaviour against blobs published before the change.
}

export interface KilItem {
  /** #90 step 10 — move provenance, from the SAME durable record the
   * briefing reads, so this row cannot contradict the briefing. */
  breadcrumb?: BreadcrumbView;
  slug: string;
  serial: number | null;
  display_number: string | null;
  name: string;
  narrative: string | null;
  summary: string | null;
  status: "open" | "closed";
  thread_slug: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  // #37/#2: the Category's phase band, for PSU phase-band grouping
  // (optional — absent on snapshots published before phases shipped;
  // null/absent => General band, mirroring the briefing).
  phase?: PhaseValue | null;
  // #4: overlay sub-category grouping (optional — absent on snapshots
  // published before the carry-into-PSU change).
  subcategory_slug?: string | null;
  subcategory_name?: string | null;
  first_seen: string | null;
  start_date: string | null;
  closed_at: string | null;
  responsible_party: {
    name: string;
    email: string | null;
    source: string | null;
    locked_at: string | null;
  } | null;
  action_items: KilAction[];
}

export interface KilCounts {
  marked_items: number;
  marked_actions: number;
  context_items: number;
}

export interface KilMarksSnapshot {
  project_slug: string;
  snapshot_at: string | null;
  items: KilItem[];
  counts?: KilCounts;
}

export async function fetchKilMarks(): Promise<KilMarksSnapshot> {
  return getJson<KilMarksSnapshot>("/kil-marks");
}

interface BriefingsListResponse {
  briefings: Array<{ run_id: string; week_end: string }>;
}

async function latestRunId(): Promise<string | null> {
  const list = await getJson<BriefingsListResponse>("/briefings");
  return list.briefings[0]?.run_id ?? null;
}

export interface BriefingsAnchor {
  runId: string | null;
  weekEnd: string | null;
  projectSlug: string;
}

async function fetchAnchor(snapshot: KilMarksSnapshot): Promise<BriefingsAnchor> {
  // Soft-fail: in fixture mode the SPA serves from a static server that
  // doesn't expose /briefings, so getJson throws. Fall back to nulls so
  // the Generate flow still renders the form with a "draft" default
  // filename — operator can edit at generation time per Decision 5.9.
  try {
    const list = await getJson<BriefingsListResponse>("/briefings");
    const first = list.briefings[0];
    return {
      runId: first?.run_id ?? null,
      weekEnd: first?.week_end ?? null,
      projectSlug: snapshot.project_slug,
    };
  } catch {
    return {
      runId: null,
      weekEnd: null,
      projectSlug: snapshot.project_slug,
    };
  }
}

// Post an Action mark toggle. Uses the existing correction-kind
// contract: action_kil_mark / action_kil_unmark — target type
// "action_item" (id encoded as
// `thread:<t>/work_item:<wi>/action_item:<ai>`).
// #58 (PSU = Actions only): there is no Item-level mark toggle — an
// Item enters the Project Status Update solely via a marked Action,
// which auto-pulls the parent Item + Category into the snapshot.
export async function toggleActionMark(
  threadSlug: string | null,
  workItemSlug: string,
  aiSlug: string,
  marked: boolean,
): Promise<void> {
  const runId = await latestRunId();
  if (!runId) {
    throw new Error("No briefing run to anchor the mark event against.");
  }
  // Target id mirrors portal/src/target_id.ts conventions.
  const targetId = threadSlug
    ? `thread:${threadSlug}/work_item:${workItemSlug}/action_item:${aiSlug}`
    : `unthreaded/work_item:${workItemSlug}/action_item:${aiSlug}`;
  await postJson(`/feedback/${encodeURIComponent(runId)}`, {
    kind: marked ? "action_kil_mark" : "action_kil_unmark",
    target: { type: "action_item", id: targetId },
    payload: {},
  });
}

// --- Default filename per Decision 5.9 -------------------------------------
//   `YYYY.MMDD ProjectDisplayName-PSU.xlsx` (#181-C2, client 2026-07-21: the
//   client-facing name is "Project Status Update" — the internal "Key Issues"
//   name is retired on client surfaces per Decision 8.2. #111b — Excel is the
//   format going forward; the download handler swaps the extension to match
//   whatever the record actually serves, so historical Word records still
//   download .docx.)
//   date = period-end of the briefing run (NOT generation timestamp)
export function defaultGeneratedFilename(
  weekEnd: string | null, projectName: string,
): string {
  const safeName = projectName.replace(/[^A-Za-z0-9_-]/g, "");
  const dateStr = weekEnd ? weekEnd.replace(/-/g, "").replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2$3") : "draft";
  return `${dateStr} ${safeName}-PSU.xlsx`;
}

// --- Renderer ---------------------------------------------------------------

export function renderKilMarks(
  root: HTMLElement,
  snapshot: KilMarksSnapshot,
  projectName: string,
  generations: KilGenerationListItem[],
  onMutated: () => void | Promise<void>,
): void {
  root.innerHTML = "";

  // Decision 12.12 (2026-05-28, client feedback): KIL view adopts the
  // briefing page's layout — left rail (Past Generations card) +
  // main content (header + Generate widget + marked items). Mirrors
  // the briefing page's .layout/.left-rail/.viewer structure so the
  // two tabs feel like peers, not cousins.
  //
  // The layout grid only kicks in when there's a left-rail card to
  // place there (generations.length > 0). Otherwise the main content
  // renders without the grid so we don't reserve a 210px empty
  // column. The `.kil-layout` class is the discriminator.
  // The left rail is now ALWAYS present — it hosts the Generate action at its
  // top (operator placement, 2026-06-27), with the Past Generations card below
  // it when any generations exist. The Generate button itself is built further
  // down (it needs the snapshot/anchor) and inserted at the rail's top there.
  const layout = document.createElement("div");
  layout.className = "layout kil-layout";

  const leftRail = document.createElement("div");
  leftRail.className = "left-rail";
  if (generations.length > 0) {
    const genCard = document.createElement("aside");
    genCard.className = "sidebar kil-generations-sidebar";
    renderGenerationsSection(genCard, generations);
    leftRail.appendChild(genCard);
  }
  layout.appendChild(leftRail);

  // --- Main content (replaces the old top-level <main> body) ---
  const main = document.createElement("div");
  main.className = "kil-marks-view viewer";

  const heading = document.createElement("h2");
  heading.textContent = "Project Status Update";
  main.appendChild(heading);

  const subtitle = document.createElement("p");
  subtitle.className = "kil-marks-subtitle";
  const counts = snapshot.counts ?? {
    marked_items: 0, marked_actions: 0, context_items: 0,
  };
  if (snapshot.snapshot_at) {
    subtitle.textContent =
      `Draft preview — ${counts.marked_items} marked Item${counts.marked_items === 1 ? "" : "s"}, ` +
      `${counts.marked_actions} marked Action${counts.marked_actions === 1 ? "" : "s"}. ` +
      `Snapshot ${snapshot.snapshot_at}.`;
  } else {
    subtitle.textContent = "Draft preview — no marks yet.";
  }
  main.appendChild(subtitle);

  // Generate button — clicks expand an inline form (filename + label)
  // per Decision 5.9 ("operator can edit the filename at generation
  // time"). On submit: POST to /kil-generations, trigger an
  // auto-download via a programmatic anchor click (Decision 12.13,
  // 2026-05-28 — was "navigate to Past Generations and click
  // Download" which required a page reload; client flagged the
  // disconnect), then refresh in place via onMutated().
  const generateRow = document.createElement("div");
  generateRow.className = "kil-marks-generate";
  const generateBtn = document.createElement("button");
  generateBtn.type = "button";
  generateBtn.className = "kil-generate-btn";
  generateBtn.textContent = "Generate Project Status Update";
  generateBtn.disabled = snapshot.items.length === 0;
  generateBtn.addEventListener("click", async () => {
    const anchor = await fetchAnchor(snapshot);
    const defaultFilename = defaultGeneratedFilename(anchor.weekEnd, projectName);
    renderGenerateForm(generateRow, defaultFilename, async (filename, label) => {
      try {
        const result = await createKilGeneration(filename, label);
        // Decision 12.13: auto-download the freshly archived doc. MUST go
        // through the authenticated fetch layer (downloadFile) — a bare anchor
        // can't send X-Project-Slug and 400s post-#94 (see api.ts). The Worker
        // defaults to Excel when no ?format is given.
        // The download is decoupled from the list refresh: the generation is
        // archived regardless of whether the browser download starts, so a
        // download error must NOT hide the new row or read as "Generation
        // failed" (the row's Download link is the fallback).
        try {
          await downloadFile(
            kilGenerationDownloadUrl(result.id), result.filename,
          );
        } catch {
          // Generation succeeded; only the auto-download failed — non-fatal.
        }
        await onMutated();
      } catch (e) {
        const err = document.createElement("p");
        err.className = "closed-item-error";
        // Surface specific error reasons when the Worker returns
        // structured errors. The most common: `no-rendered-doc` —
        // returned when no kil-doc/<slug>/latest exists in KV yet.
        // The agent populates that key during cron's stage_synthesize
        // via publish_kil_doc_snapshot; if marks were added between
        // cron runs (via the SPA pencil → Worker snapshot mutation),
        // the kil-doc binary lags until the next cron renders it
        // from the absorbed state.db.
        if (e instanceof ApiError && e.status === 409) {
          // #181 (web≠download staleness gate): a mark landed after the last
          // render, so the Worker kicked an async re-render rather than archive
          // a stale xlsx. Show it as an informational note (not a red error)
          // and let the recipient retry once the fresh doc lands.
          const body = (e.body as { error?: string } | null) ?? {};
          if (body.error === "stale-regenerating") {
            err.className = "kil-marks-subtitle";  // muted/info, not error red
            err.textContent =
              "A mark changed since the last Project Status Update was " +
              "rendered. It's regenerating from your latest marks now — click " +
              "Generate again in a few seconds.";
          } else {
            err.textContent = `Generation failed: ${body.error ?? "409"}`;
          }
        } else if (e instanceof ApiError && e.status === 404) {
          const body = (e.body as { error?: string } | null) ?? {};
          if (body.error === "no-rendered-doc") {
            err.textContent =
              "No KIL .docx rendered yet. The pre-rendered doc is " +
              "refreshed each cron run from state.db marks. Marks " +
              "added in this session via the pencil are stored but " +
              "won't appear in a .docx until the next Monday cron " +
              "absorbs them (or a workflow_dispatch run completes " +
              "with the marks present in state.db).";
          } else {
            err.textContent = `Generation failed: ${body.error ?? "404"}`;
          }
        } else {
          err.textContent = `Generation failed: ${e instanceof Error ? e.message : String(e)}`;
        }
        generateRow.appendChild(err);
      }
    });
  });
  generateRow.appendChild(generateBtn);
  // Top of the left rail, above the Past Generations card (operator placement).
  leftRail.insertBefore(generateRow, leftRail.firstChild);

  if (snapshot.items.length === 0) {
    const empty = document.createElement("p");
    empty.className = "kil-marks-empty";
    empty.textContent = "No Actions marked yet. Use the PSU toggle on an Action in the briefing view — its Item and Category come along automatically.";
    main.appendChild(empty);
    layout.appendChild(main);
    root.appendChild(layout);
    return;
  }

  // Group items by thread for hierarchical display. Insertion order =
  // snapshot order = numeric display_number order (#180), so Categories
  // read top→bottom by number.
  const byThread = new Map<string, KilItem[]>();
  for (const item of snapshot.items) {
    const key = item.thread_slug ?? "_unthreaded";
    byThread.set(key, [...(byThread.get(key) ?? []), item]);
  }
  const threadGroups = [...byThread.values()];

  // #37/#2: group the Category sections under phase-band headers when
  // phasing is active (partitionKilByPhase returns null when it isn't, and
  // the sections render flat — byte-identical to the pre-phase PSU).
  const bands = partitionKilByPhase(threadGroups);
  if (bands === null) {
    for (const items of threadGroups) {
      main.appendChild(renderKilThreadSection(items, onMutated));
    }
  } else {
    // #2: phasing active — the sticky Category headers tuck under the sticky
    // phase band (.kil-phased shifts their sticky `top` by --phase-band-height).
    main.classList.add("kil-phased");
    for (const [band, threadsInBand] of bands) {
      // A <div>, not a heading (mirrors .phase-band-header) so the PSU
      // document outline stays anchored on the Category h2s.
      const bandHeader = document.createElement("div");
      bandHeader.className = "kil-phase-header";
      bandHeader.textContent = PHASE_LABELS[band];
      main.appendChild(bandHeader);
      for (const items of threadsInBand) {
        main.appendChild(renderKilThreadSection(items, onMutated));
      }
    }
  }

  layout.appendChild(main);
  root.appendChild(layout);
}

// #37/#2: partition the Category groups into phase bands for the PSU.
// `threadGroups` is the list of per-Category Item arrays in display order.
// Returns null when phasing is INACTIVE (no Category carries a non-null,
// non-'general' phase) — the caller then renders flat, byte-identical to
// the pre-phase PSU, mirroring the briefing's "bands only when active"
// rule. Otherwise returns the non-empty bands in PHASE_ORDER, each with
// its Category groups in their original (display-number) order. Only
// non-empty bands are emitted: the briefing always shows all 4 (it is the
// full project structure), but the PSU is the marked subset, where an
// empty band header would misread as "no work in this phase" rather than
// "no marked Actions here". A Category's phase is constant across its
// Items (phase is a Category attribute), so band = the first Item's band.
export function partitionKilByPhase(
  threadGroups: KilItem[][],
): Array<[PhaseValue, KilItem[][]]> | null {
  const phasingActive = threadGroups.some(
    (items) => phaseBand(items[0]?.phase) !== "general",
  );
  if (!phasingActive) return null;
  const byBand = new Map<PhaseValue, KilItem[][]>();
  for (const items of threadGroups) {
    const band = phaseBand(items[0]?.phase);
    byBand.set(band, [...(byBand.get(band) ?? []), items]);
  }
  const out: Array<[PhaseValue, KilItem[][]]> = [];
  for (const band of PHASE_ORDER) {
    const inBand = byBand.get(band);
    if (inBand && inBand.length > 0) out.push([band, inBand]);
  }
  return out;
}

// Render one Category (thread) section: the Category <h2> header plus its
// marked Items, partitioned by sub-category (#4). Factored out of
// renderKilMarks so phase-band grouping (#37/#2) can wrap the sections.
function renderKilThreadSection(
  items: KilItem[],
  onMutated: () => void | Promise<void>,
): HTMLElement {
  const threadSection = document.createElement("section");
  threadSection.className = "kil-thread";
  // Mirror the briefing page's Category header (renderCategoryV4): an
  // <h2> with a `.path-num` number span, so the PSU page reads with the
  // same Category styling instead of a small muted section label.
  const threadHeader = document.createElement("h2");
  const first = items[0]!;
  const threadLabel = first.thread_name ?? "Cross-cutting";
  if (first.thread_display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = `${first.thread_display_number}.`;
    threadHeader.appendChild(num);
  }
  threadHeader.appendChild(document.createTextNode(threadLabel));
  threadSection.appendChild(threadHeader);
  // #4: partition the Category's marked Items by sub-category (overlay
  // grouping, mirroring the briefing's renderCategoryV4). Ungrouped Items
  // render directly under the Category; each sub-category follows under
  // its own header so a marked Action inside a sub-category shows its
  // full Category › Sub-category › Item path on the PSU surface.
  const ungrouped: KilItem[] = [];
  const bySubcat = new Map<string, { name: string; items: KilItem[] }>();
  for (const item of items) {
    const sc = item.subcategory_slug;
    if (!sc) {
      ungrouped.push(item);
      continue;
    }
    const group = bySubcat.get(sc)
      ?? { name: item.subcategory_name ?? sc, items: [] };
    group.items.push(item);
    bySubcat.set(sc, group);
  }
  for (const item of ungrouped) {
    threadSection.appendChild(renderItemCard(item, onMutated));
  }
  for (const group of bySubcat.values()) {
    // B4 (2026-06-23): wrap header + member Item cards in a containment group
    // (parity with the briefing's .subcategory-group) — the PSU previously
    // appended them as flat siblings, so the items didn't read as nested.
    const groupEl = document.createElement("section");
    groupEl.className = "kil-subcategory-group";
    const subHeader = document.createElement("h3");
    subHeader.className = "kil-subcategory-header";
    subHeader.textContent = group.name;
    groupEl.appendChild(subHeader);
    for (const item of group.items) {
      groupEl.appendChild(renderItemCard(item, onMutated));
    }
    threadSection.appendChild(groupEl);
  }
  return threadSection;
}

function renderItemCard(
  item: KilItem,
  onMutated: () => void | Promise<void>,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "kil-item-card";
  card.dataset.slug = item.slug;
  if (item.status === "closed") card.classList.add("kil-item-closed");

  const header = document.createElement("header");
  header.className = "kil-item-header";

  // #58 (PSU = Actions only): no Item-level mark toggle — every Item card
  // here is the parent of ≥1 marked Action (the only way it surfaces),
  // so the header is just the Item title + its status badges.
  const title = document.createElement("h4");
  const path = item.display_number ? `${item.display_number} ` : "";
  title.textContent = `${path}${item.name}`;
  // The number above can have CHANGED since the recipient last saw this row;
  // without the crumb this view contradicts the briefing (ruling 2026-08-10).
  if (item.breadcrumb) title.appendChild(renderBreadcrumb(item.breadcrumb));
  header.appendChild(title);

  if (item.status === "closed") {
    const closedBadge = document.createElement("span");
    closedBadge.className = "kil-item-badge kil-item-badge-closed";
    closedBadge.textContent = item.closed_at ? `Closed · ${item.closed_at}` : "Closed";
    header.appendChild(closedBadge);
  }

  card.appendChild(header);

  if (item.narrative) {
    const narrative = document.createElement("p");
    narrative.className = "kil-item-narrative";
    narrative.textContent = item.narrative;
    card.appendChild(narrative);
  }

  // Decision 12.7 (2026-05-28, client feedback #5): KIL view renders
  // Items in the compact-view content model — narrative + Responsible
  // + Started footnote, then each Action with its own title +
  // narrative + status pill + meta footnote. The Item meta is a
  // single line with `·` separators, matching the briefing
  // compact-view layout (briefings.ts renderItemV4 line ~2611).
  const itemMetaBits: string[] = [];
  if (item.responsible_party?.name) {
    itemMetaBits.push(`Responsible: ${item.responsible_party.name}`);
  }
  const startDate = item.start_date ?? item.first_seen;
  if (startDate) {
    itemMetaBits.push(`Started ${startDate}`);
  }
  if (itemMetaBits.length) {
    const meta = document.createElement("p");
    meta.className = "kil-item-meta";
    meta.textContent = itemMetaBits.join(" · ");
    card.appendChild(meta);
  }

  // "Select all" shortcut (Decision 5.4) — bulk-marks every child
  // Action of the Item. Useful when the operator wants to escalate
  // everything under a problem Item to the KIL all at once.
  // Hidden by default — see BULK_KIL_MARK_VISIBLE at top of file.
  if (BULK_KIL_MARK_VISIBLE) {
    const selectAllBtn = document.createElement("button");
    selectAllBtn.type = "button";
    selectAllBtn.className = "kil-select-all";
    selectAllBtn.textContent = "Select all child Actions";
    selectAllBtn.title =
      "Mark every child Action of this Item (Decision 5.4 bulk shortcut).";
    selectAllBtn.addEventListener("click", async () => {
      selectAllBtn.disabled = true;
      selectAllBtn.textContent = "Marking…";
      try {
        // Fetch every action under this work_item from the latest
        // briefing payload — but that's a network round-trip. Simpler:
        // POST one mark per Action currently visible in the snapshot
        // section. The next snapshot push will include any newly-marked
        // Actions that weren't already in the view; operator can re-
        // click for a second wave if needed.
        //
        // For v1, no-op when no Actions are visible on the card. The
        // shortcut's value is for Items with multiple unmarked-but-
        // discoverable Actions — that surfaces in the briefing view's
        // KIL toggle row, not this archive view. Document and surface
        // the count.
        const unmarkedInSnapshot = item.action_items.filter(
          (a) => !a.kil_marked,
        );
        if (unmarkedInSnapshot.length === 0) {
          alert(
            "All Actions visible in this Item card are already marked.\n\n" +
            "To bulk-mark unmarked Actions, use the 'Select all' control on " +
            "the Item header in the briefing view.",
          );
          selectAllBtn.disabled = false;
          selectAllBtn.textContent = "Select all child Actions";
          return;
        }
        for (const ai of unmarkedInSnapshot) {
          await toggleActionMark(item.thread_slug, item.slug, ai.ai_slug, true);
        }
        await onMutated();
      } catch (e) {
        selectAllBtn.disabled = false;
        selectAllBtn.textContent = "Select all child Actions";
        console.error("Select all failed:", e);
      }
    });
    card.appendChild(selectAllBtn);
  }

  if (item.action_items.length > 0) {
    const actionsList = document.createElement("ul");
    actionsList.className = "kil-action-list";
    for (const ai of item.action_items) {
      actionsList.appendChild(renderActionRow(item, ai, onMutated));
    }
    card.appendChild(actionsList);
  }

  return card;
}

// Mirror the briefing's `.action-row.<short>` left-edge modifier
// classes (Decision 12.12, 2026-05-28). Same priority palette as
// `priorityClass` in briefings.ts, but emits a KIL-scoped suffix so
// the CSS rules can't collide with the briefing's own action rows
// (which the recipient may see open in another tab).
function kilActionPriClass(priority: string): string {
  switch (priority) {
    case "Critical":         return "kil-action-critical-pri";
    case "Delay Potential":  return "kil-action-delay-pri";
    case "On Hold":          return "kil-action-hold-pri";
    case "Informational":    return "kil-action-info-pri";
    default:                 return "kil-action-active-pri";
  }
}

function renderActionRow(
  item: KilItem,
  ai: KilAction,
  onMutated: () => void | Promise<void>,
): HTMLElement {
  const li = document.createElement("li");
  // Status modifier (kil-action-open / kil-action-closed) +
  // priority modifier (Decision 12.12 — drives the left-edge color
  // mirroring `.action-row.<short>`).
  li.className = [
    "kil-action",
    `kil-action-${ai.status}`,
    kilActionPriClass(ai.priority_status),
  ].join(" ");
  li.dataset.slug = ai.ai_slug;

  // KIL mark/unmark button. Operator direction 2026-06-12: moved to the
  // RIGHT edge (the position the briefing pencil occupies) and shrunk so
  // it doesn't compete with the Action content. Appended last — see the
  // grid (1fr auto auto) → [body] [priority] [mark].
  const markBtn = document.createElement("button");
  markBtn.type = "button";
  markBtn.className = ai.kil_marked ? "kil-mark active" : "kil-mark";
  markBtn.title = ai.kil_marked
    ? "Click to unmark this Action"
    : "Click to mark this Action";
  markBtn.textContent = ai.kil_marked ? "✓" : "+";
  markBtn.addEventListener("click", async () => {
    markBtn.disabled = true;
    try {
      await toggleActionMark(
        item.thread_slug, item.slug, ai.ai_slug, !ai.kil_marked,
      );
      await onMutated();
    } catch (e) {
      markBtn.disabled = false;
      console.error("Action mark toggle failed:", e);
    }
  });

  // Column 1: text body — title + narrative + meta footnote.
  // Stacks vertically; matches the briefing's `.action-row` content
  // column.
  const body = document.createElement("div");
  body.className = "kil-action-body";

  const text = document.createElement("div");
  text.className = "kil-action-text";
  const path = ai.display_number ? `${ai.display_number} ` : "";
  text.textContent = `${path}${ai.text}`;
  if (ai.breadcrumb) text.appendChild(renderBreadcrumb(ai.breadcrumb));
  if (ai.status === "closed") text.classList.add("kil-action-closed-text");
  body.appendChild(text);

  if (ai.narrative) {
    const narr = document.createElement("p");
    narr.className = "kil-action-narrative";
    narr.textContent = ai.narrative;
    body.appendChild(narr);
  }

  // Open questions are deliberately NOT rendered on the PSU (client
  // 2026-08-12: "no questions brought forward into the PSU on screen or
  // Excel"). The PSU is a status report handed to a community board, where an
  // open question reads as an unanswered demand rather than a status; questions
  // still render on the BRIEFING, the recipient's private working surface.
  // The agent also stopped emitting `open_questions_v4` into the marks snapshot
  // (agent/portal.py `_render_index`), so this and the Excel PSU agree by
  // construction rather than by each holding a matching filter. Removing only
  // one of the two would have left the surfaces disagreeing — and a partially
  // fixed surface is worse than an unfixed one, because whichever you check
  // looks right. Supersedes #5 (2026-06-28).

  // Decision 12.7 (2026-05-28, client feedback #5): Action meta
  // footnote matches the briefing compact-view layout — `Due ·
  // Responsible · Started`. Closed-action timestamp rides along when
  // present so the KIL view keeps the tombstone signal it had before.
  // Skip Due on On Hold (matching briefings.ts §4.2 + the markdown
  // template) — On Hold Actions don't carry a due date by contract.
  const metaBits: string[] = [];
  // #59: due bit via the shared dueMetaBit helper (mirrors the briefing tree;
  // "Due date: TBD" when an open Action has none).
  const dueBit = dueMetaBit(ai.priority_status, ai.due, ai.status);
  if (dueBit) metaBits.push(dueBit);
  if (ai.assignee) metaBits.push(`Responsible: ${ai.assignee}`);
  if (ai.first_seen) metaBits.push(`Started ${ai.first_seen}`);
  if (ai.closed_at) metaBits.push(`Closed ${ai.closed_at}`);
  if (metaBits.length) {
    const meta = document.createElement("div");
    meta.className = "kil-action-meta";
    meta.textContent = metaBits.join(" · ");
    body.appendChild(meta);
  }

  li.appendChild(body);

  // Column 2: priority pill (mirrors briefing's `.action-status-pill`).
  const priorityChip = document.createElement("span");
  priorityChip.className = `priority priority-${priorityClass(ai.priority_status)}`;
  priorityChip.textContent = ai.priority_status;
  li.appendChild(priorityChip);

  // Column 3 (rightmost, pencil position): the small mark/unmark control.
  li.appendChild(markBtn);

  return li;
}

function priorityClass(status: string): string {
  return status.toLowerCase().replace(/[^a-z]+/g, "-");
}

// --- Generate form ---------------------------------------------------------

function renderGenerateForm(
  container: HTMLElement,
  defaultFilename: string,
  onSubmit: (filename: string, label: string | null) => Promise<void>,
): void {
  // Replace the Generate button with an inline form. Filename is pre-
  // filled per Decision 5.9; label is optional and free-text.
  container.innerHTML = "";
  const form = document.createElement("form");
  form.className = "kil-generate-form";

  const filenameLabel = document.createElement("label");
  filenameLabel.className = "kil-generate-field";
  const filenameLabelText = document.createElement("span");
  filenameLabelText.textContent = "Filename";
  const filenameInput = document.createElement("input");
  filenameInput.type = "text";
  filenameInput.value = defaultFilename;
  filenameInput.required = true;
  filenameInput.maxLength = 255;
  filenameLabel.appendChild(filenameLabelText);
  filenameLabel.appendChild(filenameInput);
  form.appendChild(filenameLabel);

  const labelLabel = document.createElement("label");
  labelLabel.className = "kil-generate-field";
  const labelLabelText = document.createElement("span");
  labelLabelText.textContent = "Label (optional)";
  const labelInput = document.createElement("input");
  labelInput.type = "text";
  labelInput.placeholder = "e.g. for May board mtg";
  labelInput.maxLength = 200;
  labelLabel.appendChild(labelLabelText);
  labelLabel.appendChild(labelInput);
  form.appendChild(labelLabel);

  const buttons = document.createElement("div");
  buttons.className = "kil-generate-form-buttons";
  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "kil-generate-btn";
  submit.textContent = "Submit and Download";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "kil-generate-cancel";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => {
    // Restore the original "Generate" button. Re-rendering via the
    // parent view's onMutated would refetch unnecessarily; toggling
    // the button-in-place is cheaper.
    container.innerHTML = "";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "kil-generate-btn";
    btn.textContent = "Generate Project Status Update";
    btn.addEventListener("click", () => {
      renderGenerateForm(container, defaultFilename, onSubmit);
    });
    container.appendChild(btn);
  });
  buttons.appendChild(submit);
  buttons.appendChild(cancel);
  form.appendChild(buttons);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    submit.textContent = "Creating…";
    cancel.disabled = true;
    const fname = filenameInput.value.trim();
    const label = labelInput.value.trim() || null;
    if (!fname) {
      submit.disabled = false;
      submit.textContent = "Submit and Download";
      cancel.disabled = false;
      filenameInput.focus();
      return;
    }
    await onSubmit(fname, label);
  });

  container.appendChild(form);
  filenameInput.focus();
  filenameInput.select();
}

// --- Generations archive list ---------------------------------------------

// Decision 12.14 (2026-05-28, operator follow-up): generated-logs
// card is intentionally minimal — one row per generation showing
// only the filename + a small download icon. Hovering the row
// surfaces every other detail (label, generated date/by, week
// ending, item/action counts) via a native `title` tooltip. The
// rail width is too tight for an info-dense layout; collapsing
// to filename + icon keeps the card scannable.
function renderGenerationsSection(
  parent: HTMLElement,
  generations: KilGenerationListItem[],
): void {
  const header = document.createElement("h2");
  header.textContent = `Generated Logs (${generations.length})`;
  parent.appendChild(header);

  const list = document.createElement("ul");
  list.className = "kil-generations-list";
  for (const gen of generations) {
    const li = document.createElement("li");
    li.className = "kil-generation-row";

    // Build the hover-tooltip text from the same metadata the
    // previous expanded layout rendered visibly. `title` attributes
    // get separator-aware line wrapping in most browsers; falling
    // back to ` · ` keeps it readable on one or two lines.
    const tipParts: string[] = [];
    if (gen.label) tipParts.push(gen.label);
    tipParts.push(`Generated ${formatGeneratedAt(gen.generated_at)}`);
    tipParts.push(`by ${gen.generated_by}`);
    if (gen.week_end) tipParts.push(`week ending ${gen.week_end}`);
    if (typeof gen.marked_items === "number") {
      tipParts.push(
        `${gen.marked_items} item${gen.marked_items === 1 ? "" : "s"}`,
      );
    }
    if (typeof gen.marked_actions === "number") {
      tipParts.push(
        `${gen.marked_actions} action${gen.marked_actions === 1 ? "" : "s"}`,
      );
    }
    const tipText = tipParts.join("\n");

    const fname = document.createElement("span");
    fname.className = "kil-generation-filename";
    fname.textContent = gen.filename;
    fname.title = tipText;
    li.appendChild(fname);

    // #111b — a SINGLE download icon serving whichever format this generation
    // carries: Excel when present (new records), else Word (historical records).
    const asXlsx = !!gen.has_xlsx;
    if (asXlsx || gen.has_docx) {
      const stem = gen.filename.replace(/\.(docx|xlsx)$/i, "");
      const fmtName = stem + (asXlsx ? ".xlsx" : ".docx");
      const fmtLabel = asXlsx ? "Excel" : "Word";
      const path = kilGenerationDownloadUrl(gen.id) + (asXlsx ? "?format=xlsx" : "");
      const dl = document.createElement("a");
      dl.className = "kil-generation-download";
      // Download through the authenticated fetch layer, NOT a bare href — a
      // bare-anchor GET can't send X-Project-Slug and 400s post-#94 (api.ts).
      dl.href = "#";
      dl.setAttribute("role", "button");
      dl.setAttribute("aria-label", `Download ${fmtName} (${fmtLabel})`);
      dl.title = `Download ${fmtName} (${fmtLabel})`;
      wireDownloadControl(dl, path, fmtName, (msg) => {
        let errEl = li.querySelector<HTMLElement>(".kil-generation-error");
        if (!errEl) {
          errEl = document.createElement("div");
          errEl.className = "kil-generation-error closed-item-error";
          li.appendChild(errEl);
        }
        errEl.textContent = msg;
      });
      // Same 16x16 download glyph the shell-header uses for briefing downloads
      // (see app.ts buildDownloadIcon) — consistent affordance across surfaces.
      dl.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
        '<polyline points="7 10 12 15 17 10"/>' +
        '<line x1="12" y1="15" x2="12" y2="3"/>' +
        "</svg>";
      li.appendChild(dl);
    }

    list.appendChild(li);
  }
  parent.appendChild(list);
}

export function formatGeneratedAt(iso: string): string {
  // Display as `YYYY-MM-DD HH:MM` (local). Falls back to the raw ISO
  // when parsing fails — safer than throwing on malformed inputs.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}
